// @ts-nocheck
import { Box, Link, Typography, Popover as MuiPopover } from '@mui/material';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * TableTextCell
 *
 * Text-based replacement for a chip-based cell. Renders the first entry inline
 * (comma-separated if you choose to join multiple visible entries) plus a
 * "+N more" affordance that opens a popover listing every entry.
 *
 * When `isRedirectionEnabled` is true, each entry is rendered as an anchor.
 * It carries no visual "link" styling at rest (no color, no underline) —
 * on hover it turns primary-colored and underlined, per design.
 */

/** Extracts the display label from an item regardless of mode. */
const getLabel = (item, isRedirectionEnabled) => {
  if (isRedirectionEnabled) {
    return item?.label || item?.value || '';
  }

  if (typeof item === 'string') {
    return item;
  }

  return item?.label || item?.value || '';
};

/** Extracts a stable key from an item regardless of mode. */
const getKey = (item, isRedirectionEnabled) => {
  if (isRedirectionEnabled) {
    return item?.value || item?.label;
  }

  if (typeof item === 'string') {
    return item;
  }

  return item?.value || item?.label;
};

/** Shared hover styling: plain text at rest, primary + underline on hover. */
const hoverLinkSx = {
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'color 120ms ease',
  color: 'primary.main',

  '&:hover': {
    textDecoration: 'underline',
  },

  '&:focus-visible': {
    color: 'primary.main',
    textDecoration: 'underline',
    outlineOffset: 2,
  },
};

const getTextClampSx = (noWrap) => ({
  display: noWrap ? 'inline-block' : 'block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: noWrap ? 'nowrap' : 'normal',
  wordBreak: noWrap ? 'normal' : 'break-word',
  verticalAlign: 'bottom',
});

const sharedEntryTextSx = {
  typography: 'subtitle2',
  lineHeight: '18px',
};

/** A single text entry — plain span normally, anchor when redirection is on. */
const EntryText = ({ item, isRedirectionEnabled, noWrap = true, sx = {} }) => {
  const label = getLabel(item, isRedirectionEnabled);

  if (isRedirectionEnabled) {
    const handleClick = useCallback((e) => {
      e.stopPropagation();
    }, []);

    return (
      <Link
        href={item?.value}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        title={label}
        onClick={handleClick}
        sx={{
          ...sharedEntryTextSx,
          ...getTextClampSx(noWrap),
          ...hoverLinkSx,
          ...sx,
        }}
      >
        {label}
      </Link>
    );
  }

  return (
    <Typography
      component="span"
      variant="subtitle2"
      title={label}
      noWrap={noWrap}
      sx={{ ...sharedEntryTextSx, ...getTextClampSx(noWrap), ...sx }}
    >
      {label}
    </Typography>
  );
};

EntryText.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ]).isRequired,
  isRedirectionEnabled: PropTypes.bool,
  noWrap: PropTypes.bool,
  sx: PropTypes.shape({}),
};

const TableTextCell = ({ entries, isRedirectionEnabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'table-text-cell-popover' : undefined;

  const handleOpen = useCallback(
    (event) => {
      if (entries?.slice(1)?.length > 0) {
        setAnchorEl(event?.currentTarget);
      }
    },
    [entries]
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  if (!entries?.length) {
    return <Typography variant="body2">-</Typography>;
  }

  const [first] = entries;
  const remainingCount = entries.length - 1;

  return (
    <Box width="100%">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
          width: '100%',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            width: 'fit-content',
            maxWidth: remainingCount > 0 ? 'calc(100% - 70px)' : '100%',
            flex: '0 1 fit-content',
          }}
        >
          <EntryText item={first} isRedirectionEnabled={isRedirectionEnabled} />
        </Box>

        {remainingCount > 0 && (
          <Box
            display="flex"
            alignItems="center"
            height="100%"
            sx={{ cursor: 'pointer' }}
            onClick={handleOpen}
            title={`View ${remainingCount} more`}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'fit-content',
                height: '20px',
                padding: '2px 10px',
                gap: '4px',
                borderRadius: '4px',
                border: (theme) =>
                  `0.5px solid ${theme.palette?.other?.MasterFilterFontColor}`,
                background: (theme) => theme.palette?.other?.topNavSelectionBg,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                component="span"
                variant="body4"
                sx={{
                  color: (theme) => theme.palette?.other?.MasterFilterFontColor,
                  textAlign: 'center',
                }}
              >
                +{remainingCount} more
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <MuiPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ mt: '8px' }}
      >
        <Box
          maxHeight="30vh"
          overflow="auto"
          sx={{
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            minWidth: '120px',
          }}
        >
          {entries.map((item) => (
            <EntryText
              key={getKey(item, isRedirectionEnabled)}
              item={item}
              isRedirectionEnabled={isRedirectionEnabled}
              noWrap={false}
              sx={{ maxWidth: '260px' }}
            />
          ))}
        </Box>
      </MuiPopover>
    </Box>
  );
};

TableTextCell.propTypes = {
  entries: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  ]),
  isRedirectionEnabled: PropTypes.bool,
};

export default TableTextCell;
